import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import X from 'react-feather/dist/icons/x';
import { CONSTANTS } from '../../constants/constants';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useForm } from 'react-hook-form';
import NRControlledTextField from '../NRControlledTextField/NRControlledTextField';
import { useLocation, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { ModalMainContainer } from '../NRActionModal/NRActionModal';
import { hasValue } from '../../services/params.utils';
import { v4 as uuidv4 } from 'uuid';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AssetsContext } from '../../DataShare/NRAssetsDataShare';
import { forwardRef } from 'react';
import { ComponentContext } from '../../ContextWrappers/NRComponentContextWrapper';
import { AssetGroupContext } from '../AssetGroup/assetGroupContextWrapper';
import { buildDefaultCsvExportFilename, finalizeCsvExportFileName, getCsvExportDownloadUrl, resolveCsvExportRoute } from './csvExportFilename.utils';

const { CANCEL, CLOSE_TOOLTIP } = CONSTANTS.CONFIRMATION_MODAL;
const { CREATED } = CONSTANTS.TRANSACTION_ASSET.LOCAL_STATUS;
const { ALL_RESULTS, CURRENT_PAGE } = CONSTANTS.CSV_EXPORT_SCOPE;

function NRExportToCsvContent(
  { selectAllFetchConfig, totalCount, handleClose, customCSVExportRouteName, specialCase = false, isOpen = true, exportScopeConfig, fileName },
  ref
) {
  const theme = useTheme();
  const { tasks, setTasks, currentAsset } = useContext(AssetsContext);
  const { asset } = useContext(ComponentContext);
  const { assetGroups } = useContext(AssetGroupContext);
  const location = useLocation();
  const { groupId } = useParams();

  const { apiRoute, tableSlug } = useMemo(
    () => resolveCsvExportRoute({ pathname: location.pathname, customCSVExportRouteName }),
    [location.pathname, customCSVExportRouteName]
  );

  const downloadUrl = getCsvExportDownloadUrl(apiRoute);

  const groupName = useMemo(() => {
    if (!groupId || !assetGroups?.length) return null;
    return assetGroups.find(group => group.id === groupId)?.name ?? null;
  }, [groupId, assetGroups]);

  const assetName = asset?.name || currentAsset?.name || null;

  const defaultFileName = useMemo(
    () =>
      fileName ||
      buildDefaultCsvExportFilename({
        groupName,
        assetName,
        tableSlug
      }),
    [fileName, groupName, assetName, tableSlug]
  );

  const { filter } = selectAllFetchConfig?.qlArgs ?? {};

  const filterHasValue = hasValue(filter) && Object.keys(filter).some(key => filter[key]);

  const methods = useForm({
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: {
      fileName: defaultFileName
    }
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty }
  } = methods;

  const wasOpenRef = useRef(false);
  const [exportScope, setExportScope] = useState(ALL_RESULTS);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;

    if (justOpened) {
      setExportScope(ALL_RESULTS);
    }

    // Reset on open, and keep syncing the async-resolved default name until the
    // user edits the field, but never clobber a value the user has typed.
    if (justOpened || !isDirty) {
      reset({ fileName: defaultFileName });
    }
  }, [isOpen, defaultFileName, isDirty, reset]);

  const onSubmitHandler = form => {
    const fileName = finalizeCsvExportFileName(form.fileName, defaultFileName);
    // The current page is exported by sending the cursor the table is showing; all results omit it.
    const scopedArgs = exportScope === CURRENT_PAGE && exportScopeConfig?.currentPageCursor ? { cursor: exportScopeConfig.currentPageCursor } : {};

    let taskParams = {
      id: uuidv4(),
      type: CONSTANTS.TASKS.TYPE.DOWNLOAD_HTTP_POST,
      status: CREATED,
      fileName,
      downloadUrl,
      body: { fileName, ...selectAllFetchConfig?.qlArgs, ...scopedArgs },
      headers: {},
      onSuccess: () => {},
      onError: () => {}
    };
    setTasks([taskParams, ...tasks]);
    handleClose();
  };

  return (
    <form ref={ref} tabIndex={-1} noValidate autoComplete="off" onSubmit={handleSubmit(onSubmitHandler)}>
      <ModalMainContainer width={500}>
        <Card elevation={0}>
          <CardHeader
            sx={{ paddingX: 3, paddingY: 2 }}
            title={<Typography id="nr-export-csv-title" variant={'h3'} children={'Export to CSV'} />}
            action={
              <Tooltip title={CLOSE_TOOLTIP} placement="right">
                <IconButton aria-label="Close" onClick={handleClose} size="large" autoFocus>
                  <X />
                </IconButton>
              </Tooltip>
            }
          />
          <CardContent sx={{ paddingX: 3, paddingBottom: 6, paddingTop: 3 }}>
            <FormControl fullWidth={true} sx={{ overflowX: 'auto', paddingTop: 1, marginTop: -1 }}>
              <NRControlledTextField
                variant="outlined"
                size="small"
                id="file-name"
                name="fileName"
                key="fileName"
                className={'classes.nameInput'}
                label={'File name'}
                helperText={(errors && errors['fileName']?.message) || '*Required'}
                control={control}
                rules={{
                  required: CONSTANTS.VALIDATIONS.REQUIRED
                }}
                error={!!errors['fileName']}
              />
            </FormControl>
            {!!exportScopeConfig ? (
              <RadioGroup sx={{ paddingTop: 3 }} value={exportScope} onChange={event => setExportScope(event.target.value)}>
                <FormControlLabel
                  value={ALL_RESULTS}
                  control={<Radio color="secondary" />}
                  label={<Typography variant={'body1'} children={`All search results ${totalCount ? `(${totalCount})` : ''}`} />}
                />
                <FormControlLabel
                  value={CURRENT_PAGE}
                  control={<Radio color="secondary" />}
                  label={
                    <Typography
                      variant={'body1'}
                      children={`Current page only ${exportScopeConfig.currentPageCount ? `(${exportScopeConfig.currentPageCount})` : ''}`}
                    />
                  }
                />
              </RadioGroup>
            ) : (
              <Typography
                sx={{ paddingTop: 3, color: theme.palette.text.secondary }}
                variant={'body1'}
                children={`${filterHasValue ? 'Filtered' : 'Entire'} table ${totalCount ? `(${totalCount} rows)` : ''}`}
              />
            )}
          </CardContent>
          <CardActions
            sx={{
              paddingY: 1.75,
              paddingX: 3,
              justifyContent: 'flex-end',
              '& > :not(:first-of-type)': {
                marginLeft: 2
              }
            }}
          >
            <Button variant={'outlined'} color={'secondary'} onClick={() => handleClose()} sx={{ ml: 'auto' }}>
              {CANCEL}
            </Button>
            <Button variant="contained" type="submit" color="secondary" disabled={(!totalCount || !!errors['fileName']) && !specialCase}>
              Export
            </Button>
          </CardActions>
        </Card>
      </ModalMainContainer>
    </form>
  );
}

export default forwardRef(NRExportToCsvContent);
